<script setup lang="ts">
import { CalendarDays, Check, Lock, TrendingUp } from "lucide-vue-next";
import { computed, ref, useId } from "vue";

import FocusStageTile from "@/components/focus-stages/FocusStageTile.vue";
import FocusStageTree from "@/components/focus-stages/FocusStageTree.vue";
import RlCard from "@/components/ui/RlCard.vue";
import RlPill from "@/components/ui/RlPill.vue";
import { FOCUS_STAGES, stageRankCopy } from "@/lib/focusCapacity";
import type { SessionMode } from "@/lib/focusSession";

/**
 * Session-complete screen (PE-8.7), reporting against focus as well as
 * knowledge.
 *
 * Purely presentational: every line is handed down already computed. In
 * particular `capacityOutcome` comes from `completionOutcome` in
 * `@/lib/focusCapacity` — re-deriving it here would let this screen and the
 * focus panel disagree about the same block.
 *
 * Two lines are conditional on purpose. The goal-met pill appears only when the
 * goal was met — a consolation pill ("goal not met") would turn a neutral
 * outcome into a reprimand, and PRD §8.3 rules that out. The attainment line
 * appears only when a tier actually moved; "no change" is noise, and rendering
 * it every session would train the student to ignore the line that matters.
 *
 * Nothing here celebrates loudly: no confetti, no fanfare, no streak threats.
 * The stage-up gets one `rl-pop` entrance and a sentence.
 */

const props = defineProps<{
  mode: SessionMode;
  scopeLabel: string;
  goalMet: boolean;
  minutes: number;
  questionsAnswered: number;
  exchanges: number;
  marksEarned: number;
  /** PE-7.6 focus line, verbatim from `completionOutcome`. */
  capacityOutcome: string;
  /** e.g. "Enzymes moved from Learning to Strong". Null when no tier moved. */
  attainmentChange: string | null;
  stageIndex: number;
  stageUp: boolean;
  reviewNudge: string;
}>();

defineEmits<{ close: []; again: [] }>();

/**
 * Local-only reflection (PE-8.7). It is a private note to self: it is never
 * sent to the API, never attached to an analytics event, never breadcrumbed to
 * Sentry. There is deliberately no network call anywhere in this component —
 * `localStorage` is the whole storage layer. The analytics payload guard
 * denylists `reflection`/`reflection_text` to catch a future regression here.
 *
 * Keyed by scope so the note belongs to the thing it was written about rather
 * than leaking across topics, and namespaced like the other client keys
 * (`revisionlab.*`).
 */
const REFLECTION_KEY_PREFIX = "revisionlab.session.reflection.";

function reflectionKey(scopeLabel: string): string {
  const slug = scopeLabel
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${REFLECTION_KEY_PREFIX}${slug || "general"}`;
}

function readReflection(): string {
  try {
    return window.localStorage.getItem(reflectionKey(props.scopeLabel)) ?? "";
  } catch {
    // Storage can be disabled (private mode). The textarea still works for
    // this session; only the restore is lost.
    return "";
  }
}

const reflection = ref(readReflection());
const reflectionId = useId();

function persistReflection(): void {
  try {
    const key = reflectionKey(props.scopeLabel);
    const text = reflection.value.trim();
    if (text) window.localStorage.setItem(key, reflection.value);
    else window.localStorage.removeItem(key);
  } catch {
    // Nothing to recover from: the note stays in the textarea.
  }
}

const MODE_LABEL: Record<SessionMode, string> = {
  question: "Question practice",
  tutor: "Tutor session",
  mock: "Mock exam",
};

// Built in script rather than as adjacent nodes in a gapped flex row, where a
// raw text node and an interpolation become two flex items and gain a gap.
const scopeLine = computed(
  () => `${MODE_LABEL[props.mode]} · ${props.scopeLabel}`,
);

const stage = computed(
  () =>
    FOCUS_STAGES[
      Math.min(
        FOCUS_STAGES.length - 1,
        Math.max(0, Math.trunc(props.stageIndex)),
      )
    ],
);

// The bare tree name is the rank — never "Oak stage", never "Level 4"
// (addendum §2). `stageRankCopy` is what enforces that.
const rankCopy = computed(() => stageRankCopy(stage.value));

const stats = computed(() => [
  { key: "minutes", value: props.minutes, label: "minutes focused" },
  props.mode === "tutor"
    ? { key: "exchanges", value: props.exchanges, label: "exchanges" }
    : {
        key: "questions",
        value: props.questionsAnswered,
        label: "questions answered",
      },
  { key: "marks", value: props.marksEarned, label: "marks earned" },
]);
</script>

<template>
  <RlCard>
    <div class="complete">
      <!-- Stage-up: one pop, one sentence. PRD §8.3 — no confetti, no fanfare. -->
      <div v-if="stageUp" class="stage-up rl-anim-pop" data-testid="stage-up">
        <FocusStageTile :stage="stage.index" size="hero" :sway="false" />
        <div>
          <p class="stage-up-rank">{{ rankCopy }}</p>
          <p class="stage-up-blurb">{{ stage.blurb }}</p>
        </div>
      </div>

      <div class="head-block">
        <div class="head">
          <h2 class="title">Session complete</h2>
          <RlPill v-if="goalMet" tone="success" data-testid="goal-met-pill">
            <Check :size="12" aria-hidden="true" />
            <span>Focus goal met</span>
          </RlPill>
        </div>
        <p class="scope">{{ scopeLine }}</p>
      </div>

      <div class="stats">
        <div v-for="s in stats" :key="s.key" class="stat">
          <p class="stat-value">{{ s.value }}</p>
          <p class="stat-label">{{ s.label }}</p>
        </div>
      </div>

      <!--
        Knowledge, not focus — so no stage tree here. It is not an
        AttainmentRing either: the container hands down a sentence, not a
        percentage, and a ring drawn from a guessed number would be a lie
        (rings are knowledge, trees are focus — PE-7.3.6).
      -->
      <p
        v-if="attainmentChange"
        class="line line-mint"
        data-testid="attainment-change"
      >
        <TrendingUp :size="16" aria-hidden="true" />
        <span>{{ attainmentChange }}</span>
      </p>

      <p class="line" data-testid="capacity-outcome">
        <FocusStageTree :stage="stage.index" size="inline" />
        <span>{{ capacityOutcome }}</span>
      </p>

      <!--
        data-statsig-block is redundant with the overlay root, but this is the
        one field in the product holding text the student was promised stays on
        their device. Two guards is the right number.
      -->
      <div class="reflection" data-statsig-block>
        <label class="eyebrow" :for="reflectionId">Quick reflection</label>
        <textarea
          :id="reflectionId"
          v-model="reflection"
          class="reflection-input"
          placeholder="What clicked? What's still shaky?"
          rows="3"
          @input="persistReflection"
        />
        <p class="privacy">
          <Lock :size="12" aria-hidden="true" />
          <span
            >Only you see this — it stays on your device and is never sent
            anywhere.</span
          >
        </p>
      </div>

      <!--
        A gesture toward spaced review, not a scheduler: the container writes
        one sentence from the topic it just worked. The real scheduler is
        Option B and is deferred, so nothing here queues, notifies or tracks.
      -->
      <p class="line" data-testid="review-nudge">
        <CalendarDays :size="16" aria-hidden="true" />
        <span>{{ reviewNudge }}</span>
      </p>

      <div class="actions">
        <button type="button" class="secondary" @click="$emit('close')">
          Close
        </button>
        <button type="button" class="primary" @click="$emit('again')">
          Start another
        </button>
      </div>
    </div>
  </RlCard>
</template>

<style scoped>
.complete {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  width: 100%;
  max-width: 480px;
}

.stage-up {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-3);
  background: var(--rl-green-50);
  border: 1px solid var(--rl-mint-300);
  border-radius: var(--radius-lg);
}

.stage-up-rank {
  margin: 0;
  font-size: var(--rl-text-lg);
  font-weight: 600;
  letter-spacing: -0.015em;
  color: var(--rl-fg-1);
}

.stage-up-blurb {
  margin: var(--sp-1) 0 0;
  font-size: var(--rl-text-base);
  line-height: 1.5;
  color: var(--rl-fg-2);
}

.head-block {
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
}

.head {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
}

.title {
  margin: 0;
  font-size: var(--rl-text-xl);
  font-weight: 600;
  letter-spacing: -0.02em;
}

.scope {
  margin: 0;
  font-size: var(--rl-text-md);
  color: var(--rl-fg-2);
}

.stats {
  display: flex;
  gap: var(--sp-3);
}

.stat {
  flex: 1;
  padding: var(--sp-3) var(--sp-4);
  background: var(--rl-bg-subtle);
  border: 1px solid var(--rl-border-subtle);
  border-radius: var(--radius-lg);
}

.stat-value {
  margin: 0;
  font-size: var(--rl-text-2xl);
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1;
}

.stat-label {
  margin: var(--sp-1) 0 0;
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
}

.line {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  margin: 0;
  padding: var(--sp-3);
  font-size: var(--rl-text-base);
  line-height: 1.5;
  color: var(--rl-fg-2);
  border: 1px solid var(--rl-border-subtle);
  border-radius: var(--radius);
}

.line :deep(svg) {
  flex: none;
  color: var(--rl-fg-3);
}

.line-mint {
  color: var(--rl-green-800);
  background: var(--rl-mint-100);
  border-color: var(--rl-mint-300);
}

.line-mint :deep(svg) {
  color: var(--rl-green-700);
}

.reflection {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.eyebrow {
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--rl-fg-3);
}

.reflection-input {
  width: 100%;
  padding: var(--sp-3);
  font-family: var(--rl-font-sans);
  font-size: var(--rl-text-md);
  line-height: 1.5;
  color: var(--rl-fg-1);
  background: var(--rl-bg);
  border: 1px solid var(--rl-border-subtle);
  border-radius: var(--radius);
  resize: vertical;
}

.reflection-input:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus);
}

.privacy {
  display: flex;
  align-items: center;
  gap: var(--sp-1);
  margin: 0;
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
}

.privacy svg {
  flex: none;
}

.actions {
  display: flex;
  gap: var(--sp-2);
}

.actions button {
  flex: 1;
}
</style>
